import { MotionAnimate, Typography } from "@components";
import {
  faArrowTrendUp,
  faInfoCircle,
  faMoneyBillTrendUp,
  faSackDollar,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shadcn-ui/tooltip";
import clsx from "clsx";
import Decimal from "decimal.js";
import { getTransitions } from "i18n";
import { Roboto_Serif } from "next/font/google";
import React, { FC, HTMLAttributes } from "react";

import { useChartStore } from "../../chart.state";

const fontSans = Roboto_Serif({ subsets: ["latin"], variable: "--font-sans" });

export const CurrentEarnings: FC<HTMLAttributes<HTMLDivElement>> = (props) => {
  const t = getTransitions();
  const { formValues } = useChartStore();
  const profitPercent = new Decimal(formValues?.profit ?? 0).dividedBy(100); // 收益率 百分比
  // 每天结算的收益
  const oneDayProfit = profitPercent
    .plus(1)
    .pow(new Decimal(1).div(250))
    .minus(1); // 每天的收益(3%=>0.03/250 = 0.00008099) 约250个交易日
  const dailyMoney = new Decimal(formValues?.money ?? 0).times(oneDayProfit); // 每日的收益
  const monthlyMoney = dailyMoney.times(20); // 每月的收益，约20个交易日
  return (
    <div {...props} className={clsx("group relative", props.className)}>
      <div className="relative z-10 m-4 space-y-2 rounded-xl border bg-background/80 p-2 backdrop-blur">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faSackDollar} />
          <Typography.Small className="">
            {t("asset_dashboard.short_term_yield")}
          </Typography.Small>
        </div>
        <div className="flex justify-center divide-x-2 text-sm *:px-2">
          <div>
            <Typography.Muted className="text-center">
              {t("common.per_day")}
            </Typography.Muted>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faArrowTrendUp} />
              <span className="font-mono">
                {oneDayProfit.times(100).toFixed(4)} %
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faMoneyBillTrendUp} />
              <span
                className={clsx(
                  "text-lg font-bold text-amber-400",
                  fontSans.variable,
                )}
              >
                {dailyMoney.toFixed(2).toString()}
              </span>
              <span>{t("common.amount_unit_symbol")}</span>
            </div>
          </div>
          <div>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-center gap-1 text-center">
                  <Typography.Muted>{t("common.per_month")}</Typography.Muted>
                  <FontAwesomeIcon
                    className="cursor-help text-muted-foreground"
                    icon={faInfoCircle}
                    size="xs"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div>约20个交易日</div>
              </TooltipContent>
            </Tooltip>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faArrowTrendUp} />
              <span className="font-mono">
                {oneDayProfit.times(100).times(30).toFixed(4)} %
              </span>
            </div>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faMoneyBillTrendUp} />
              <span
                className={clsx(
                  "text-lg font-bold text-amber-400",
                  fontSans.variable,
                )}
              >
                {monthlyMoney.toFixed(2).toString()}
              </span>
              <span>{t("common.amount_unit_symbol")}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 text-xs">
          <div>{t("asset_dashboard.profit_rate_daily")}</div>
          <Typography.InlineCode className="text-xs">
            (1 + {profitPercent.toString()})<sup>1/250</sup> - 1 ≈{" "}
            {oneDayProfit.times(100).toFixed(4).toString()} %
          </Typography.InlineCode>
        </div>
        <Typography.Muted className="text-center text-xs">
          {t("asset_dashboard.auto_update_desc")}
        </Typography.Muted>
      </div>
      {/* shadow */}
      <div className="absolute left-1/2 top-1/2 size-full -translate-x-1/2 -translate-y-1/2 py-2">
        <MotionAnimate
          animate={{ opacity: [1, 0.8, 1], scale: [0.9, 0.85, 0.9] }}
          className={clsx(
            "size-full bg-purple-900 blur-lg",
            "transition group-hover:bg-purple-600",
          )}
          initial={{ scale: 0.9 }}
          transition={{ duration: 5, repeat: Infinity }}
        />
      </div>
    </div>
  );
};

export default CurrentEarnings;
